import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Space,
  Spin,
  Row,
  Col,
  message,
  Modal,
  Radio,
} from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import {
  GetAvailableSlotsForBDA,
  CreateBooksessionForBDA,
} from "../../redux/actions/SessionAction";
import { FindLearnerByPhoneno } from "../../redux/actions/UserAction";
import { UTCToAsiaTimeConverter12hr } from "../../helper/helper";
import { localToutcTimeConverter } from "../../helper/TimeDate";

const time_zone = JSON.parse(localStorage.getItem("TIMEZONE")) || {
  timezone: "Asia/Kolkata",
};

const BDASessionBooking = () => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingLoader, setBookingLoader] = useState(false);
  const [isTeacherModalVisible, setIsTeacherModalVisible] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedSlotTeachers, setSelectedSlotTeachers] = useState([]);
  const [summary, setSummary] = useState({
    available: 0,
    booked: 0,
    unbooked: 0,
  });

  // Generate next 3 days for date selection
  const generateDates = () => {
    const dates = [];
    for (let i = 0; i < 3; i++) {
      const date = moment().add(i, "days");
      dates.push({
        date: date.format("YYYY-MM-DD"),
        display: date.format("DD ddd MMM"),
        fullDisplay: date.format("DD ddd MMM"),
      });
    }
    return dates;
  };

  const dates = generateDates();

  // Fetch available slots when date is selected or on component mount
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchAvailableSlots = () => {
    setLoading(true);
    const data = {
      duration: 30,
      sessionDate: selectedDate, // IST date
      subject: "English",
    };

    const onSuccess = (response) => {
      setLoading(false);
      if (
        response &&
        response.teacherSlots &&
        Array.isArray(response.teacherSlots)
      ) {
        setAvailableSlots(response.teacherSlots);
        // Use summary data from API response
        setSummary({
          available: response.totalSlotsForDay || 0,
          booked: response.bookSlots || 0,
          unbooked:
            (response.totalSlotsForDay || 0) - (response.bookSlots || 0),
        });
      } else {
        setAvailableSlots([]);
        setSummary({ available: 0, booked: 0, unbooked: 0 });
      }
    };

    const onError = (error) => {
      setLoading(false);
      message.error(error || "Failed to fetch available slots");
      setAvailableSlots([]);
      setSummary({ available: 0, booked: 0, unbooked: 0 });
    };

    dispatch(GetAvailableSlotsForBDA(data, onSuccess, onError));
  };

  const formatTimeSlot = (startTimeIST, endTimeIST) => {
    // API returns times in IST format (HH:mm), convert to 12-hour format
    const formatISTTo12Hour = (timeIST) => {
      const [hours, minutes] = timeIST.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };

    const start = formatISTTo12Hour(startTimeIST);
    const end = formatISTTo12Hour(endTimeIST);
    return `${start}-${end}`;
  };

  const handleBookSession = () => {
    if (!phoneNumber || !phoneNumber.trim()) {
      message.error("Please enter a phone number");
      return;
    }

    if (!selectedTimeSlot) {
      message.error("Please select a time slot");
      return;
    }

    if (!selectedTeacherId) {
      message.error("Please select a teacher for the chosen slot");
      return;
    }

    setBookingLoader(true);

    // First, find learner by phone number
    const findLearnerData = {
      phoneNumber: phoneNumber.trim(),
    };

    const onFindLearnerSuccess = (learnerId) => {
      // After finding learner, create booking session using selected slot and teacher
      const slot = selectedTimeSlot;

      if (!slot) {
        setBookingLoader(false);
        message.error("Selected slot not found");
        return;
      }

      // Use UTC times directly from the API response
      const startTimeUTC = slot.startTimeUTC
        ? slot.startTimeUTC.split("T")[1].split(".")[0].substring(0, 5)
        : "";
      const endTimeUTC = slot.endTimeUTC
        ? slot.endTimeUTC.split("T")[1].split(".")[0].substring(0, 5)
        : "";

      // Extract date from UTC time string
      const utcDate = slot.startTimeUTC
        ? slot.startTimeUTC.split("T")[0]
        : selectedDate;

      const bookingData = {
        learner: learnerId,
        teacher_: selectedTeacherId,
        startTime: startTimeUTC, // UTC
        endTime: endTimeUTC, // UTC
        sessionDuration: 30,
        sessionDate: utcDate, // UTC date
        timezone: "Asia/Kolkata",
        subject: "English",
        source: "WEB",
      };

      const onBookingSuccess = (response) => {
        setBookingLoader(false);
        message.success("Session booked successfully!");
        // Reset form
        setSelectedTimeSlot(null);
        setSelectedTeacherId(null);
        setSelectedSlotTeachers([]);
        // Refresh slots
        fetchAvailableSlots();
      };

      const onBookingError = (error) => {
        setBookingLoader(false);
        message.error(error || "Failed to book session");
      };

      dispatch(
        CreateBooksessionForBDA(bookingData, onBookingSuccess, onBookingError),
      );
    };

    const onFindLearnerError = () => {
      setBookingLoader(false);
      message.error("Learner not found. Please check the phone number.");
    };

    dispatch(
      FindLearnerByPhoneno(
        findLearnerData,
        onFindLearnerSuccess,
        onFindLearnerError,
      ),
    );
  };

  return (
    <div>
      <PageBreadcrumb route={["Session", "BDA Session Booking"]} />

      <Card>
        <div style={{ marginBottom: "2rem" }}>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "1rem",
            }}
          >
            Available English demo slots
          </h1>

          {/* Summary Section */}
          <Card
            style={{
              backgroundColor: "#f5f5f5",
              marginBottom: "2rem",
              borderRadius: "4px",
            }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <div>Available slots: {summary.available}</div>
              </Col>
              <Col span={8}>
                <div>Booked slots: {summary.booked}</div>
              </Col>
              <Col span={8}>
                <div>Unbooked slots: {summary.unbooked}</div>
              </Col>
            </Row>
          </Card>

          {/* Phone Number Input */}
          <div style={{ marginBottom: "2rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Learner Phone Number
            </label>
            <Input
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
          </div>

          {/* Date Selection */}
          <div style={{ marginBottom: "2rem" }}>
            <h3 style={{ marginBottom: "1rem", fontWeight: "500" }}>
              Select a Date
            </h3>
            <Space wrap>
              {dates.map((dateObj) => (
                <Button
                  key={dateObj.date}
                  type={selectedDate === dateObj.date ? "primary" : "default"}
                  onClick={() => setSelectedDate(dateObj.date)}
                  style={{
                    borderColor:
                      selectedDate === dateObj.date ? "#20b2aa" : "#d9d9d9",
                    backgroundColor:
                      selectedDate === dateObj.date ? "#e0f7f6" : "#fff",
                    color: selectedDate === dateObj.date ? "#20b2aa" : "#000",
                  }}
                >
                  {dateObj.fullDisplay}
                </Button>
              ))}
            </Space>
          </div>

          {/* Time Slot Selection */}
          {selectedDate && (
            <div>
              <h3 style={{ marginBottom: "1rem", fontWeight: "500" }}>
                Select a Time slot
              </h3>
              {loading ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <Spin size="large" />
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                    marginBottom: "2rem",
                  }}
                >
                  {availableSlots.map((slot, index) => {
                    const timeDisplay = formatTimeSlot(
                      slot.startTimeIST,
                      slot.endTimeIST,
                    );
                    const isSelected =
                      selectedTimeSlot?.startTimeIST === slot.startTimeIST &&
                      selectedTimeSlot?.endTimeIST === slot.endTimeIST;
                    const isAvailable = slot.availableSlots > 0;

                    return (
                      <Button
                        key={index}
                        type={isSelected ? "primary" : "default"}
                        disabled={!isAvailable}
                        onClick={() => {
                          if (isAvailable) {
                            setSelectedTimeSlot(slot);
                            setSelectedTeacherId(null);
                            setSelectedSlotTeachers(slot.teachers || []);
                            setIsTeacherModalVisible(true);
                          }
                        }}
                        style={{
                          height: "auto",
                          padding: "12px",
                          borderColor: isSelected ? "#20b2aa" : "#d9d9d9",
                          backgroundColor: isSelected ? "#e0f7f6" : "#fff",
                          color: isSelected ? "#20b2aa" : "#000",
                          opacity: isAvailable ? 1 : 0.5,
                        }}
                      >
                        <div>{timeDisplay}</div>
                        <div
                          style={{
                            color:
                              slot.availableSlots === 0 ? "#ff4d4f" : "#ff4d4f",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          ({slot.availableSlots})
                        </div>
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Book Button */}
          <Button
            type="primary"
            size="large"
            onClick={handleBookSession}
            loading={bookingLoader}
            disabled={
              !selectedTimeSlot || !selectedTeacherId || !phoneNumber.trim()
            }
            style={{
              backgroundColor: "#20b2aa",
              borderColor: "#20b2aa",
              minWidth: "150px",
            }}
          >
            Book Session
          </Button>
        </div>
      </Card>

      <Modal
        title="Select Teacher"
        visible={isTeacherModalVisible}
        onCancel={() => {
          setIsTeacherModalVisible(false);
          // keep selectedTimeSlot but allow changing teacher later
        }}
        onOk={() => {
          if (!selectedTeacherId) {
            message.error("Please select a teacher");
            return;
          }
          setIsTeacherModalVisible(false);
        }}
        okButtonProps={{ disabled: !selectedTeacherId }}
      >
        {selectedSlotTeachers.length === 0 ? (
          <p>No teachers available for this slot.</p>
        ) : (
          <Radio.Group
            onChange={(e) => setSelectedTeacherId(e.target.value)}
            value={selectedTeacherId}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              {selectedSlotTeachers.map((t) => (
                <Radio key={t.teacherId} value={t.teacherId}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>{t.name}</span>
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )}
      </Modal>
    </div>
  );
};

export default BDASessionBooking;
